import { Injectable } from '@angular/core';
import { Vehicle } from '../interface/vehicle';
import { environment } from '../../environments/environment';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { catchError, filter, first, map, switchMap } from 'rxjs/operators';
import { DataService } from './data.service';
import { VehicleUtilities } from '../class/vehicle-utilities';
import { LocalStorageService } from './local.storage.service';
import { firstValueFrom, Observable, of, throwError } from 'rxjs';
import { Utilities } from '../class/utilities';
import { BOOLEAN_ONE } from '../shared/common-enums/boolean-one';
import { ApiPaginatedVehicleQueryParams, ApiPaginatedVehicleResponse, ApiVehiclePaginatedResponse, PaginationService } from './pagination.service';
import { API_ERRORS } from '../shared/lists/errors';
import { MatDialog } from '@angular/material/dialog';
import { InfocarLimitComponent } from '../component/page/company/popups/infocar-limit/infocar-limit.component';

@Injectable({
  providedIn: 'root'
})
export class VehicleService {
  private _isFromMatrix = true;
  private vehicles = new Map<string, Map<string, Vehicle>>();
  private timestamps = new Map<string, number>();

  constructor(
    private dataService: DataService,
    private http: HttpClient,
    private localStorageService: LocalStorageService,
    private dialog: MatDialog
  ) { }

  async getDefaultId(): Promise<string> {
    const fromLocalStorage = this.localStorageService.get('DEFAULT_VEHICLE_ID')

    if (fromLocalStorage) {
      return fromLocalStorage
    }

    const defaulVehicle = await this.getByPlate(environment.DEFAULT_VEHICLE_PLATE);

    this.localStorageService.set('DEFAULT_VEHICLE_ID', defaulVehicle.id)

    return defaulVehicle.id
  }

  async get(id: string): Promise<Vehicle> {
    const url = `${environment.mkgoURL}/api/v1/vehicles/${id}`;
    const header = await firstValueFrom(this.dataService.httpOptions(this._isFromMatrix));
    const vehicle: Vehicle = await this.http.get(url, header).pipe(first())
      .toPromise();
    return VehicleUtilities.complyApp(vehicle);
  }

  async getByPlate(plate: string): Promise<Vehicle> {
    const formtedPlate = Utilities.removeSpecialCharacters(plate).toUpperCase();
    const url = `${environment.mkgoURL}/api/v1/vehicles/plate/${formtedPlate}`;
    const header = await firstValueFrom(this.dataService.httpOptions(this._isFromMatrix));
    const vehicle: Vehicle = await this.http.get(url, header).pipe(first()).toPromise();
    return VehicleUtilities.complyApp(vehicle);
  }

  async getByPlateInfocar(plate: string): Promise<Vehicle> {
    const url = `${environment.mkgoURL}/api/v1/vehicles/infocar/${plate}`;
    const header = await firstValueFrom(this.dataService.httpOptions(this._isFromMatrix));
    
    try {
      const vehicle: Vehicle = await firstValueFrom(
        this.http.get(url, header).pipe(
          catchError((error: HttpErrorResponse) => {
            // Se o erro for limite atingido, exibir dialog
            if (error.status === 400 && error.error?.error?.includes('Limite de consultas InfoCar atingido')) {
              this.dialog.open(InfocarLimitComponent, { width: '500px' });
              return throwError(() => new Error('Limite de consultas InfoCar atingido para este mês'));
            }
            return throwError(() => error);
          })
        )
      );
      const appVehicle = VehicleUtilities.complyApp(vehicle);
      return appVehicle;
    } catch (error) {
      throw error;
    }
  }


  /** @todo refactor */
  async getAll(withDefault?: boolean): Promise<Map<string, Vehicle>> {
    const company = await firstValueFrom(this.dataService.company$.pipe(filter((c) => !!c)));
    let timestamp = this.timestamps.get(company.id) || 0;
    const url = `${environment.mkgoURL}/api/v1/vehicles`;
    const options = await firstValueFrom(this.dataService.httpOptions(this._isFromMatrix));
    options.headers = options.headers.set('timestamp', timestamp.toString())
    const response: any = await this.http.get(url, options).pipe(first()).toPromise();
    const vehicles = this.vehicles.get(company.id) || new Map<string, Vehicle>();
    this.vehicles.set(company.id, vehicles);
    ((response.vehicles || []) as Vehicle[]).reverse().forEach(vehicle => {
      VehicleUtilities.complyApp(vehicle);
      timestamp = Math.max(timestamp, new Date(vehicle.lastUpdate || 0).getTime());
      if (VehicleUtilities.isDefault(vehicle.id)) {
        if (withDefault) {
          vehicles.set(vehicle.id, vehicle);
        }
      } else {
        vehicles.set(vehicle.id, vehicle);
      }
    });
    this.timestamps.set(company.id, timestamp);
    return vehicles;
  }

  getPaginated(apiParams: ApiPaginatedVehicleQueryParams): Observable<ApiVehiclePaginatedResponse<Vehicle>> {
    const url = `${environment.mkgoURL}/api/v1/vehicles/page`;
    const params = PaginationService.getParams(apiParams);
    return this.dataService.httpOptions(this._isFromMatrix, params).pipe(
      switchMap(options => this.http.get<ApiPaginatedVehicleResponse>(url, options)),
      first(),
      catchError(err => {
        if (err.error.error === API_ERRORS.notFound) {
          return of({
            docs: [],
            hasNextPage: false,
            hasPrevPage: false,
            limit: 0,
            nextPage: null,
            page: 0,
            pagingCounter: 0,
            prevPage: null,
            totalDocs: 0,
            totalPages: 0
          })
        } else {
          throw err;
        }
      }),
      map(res => {
        if (res["vehicles"]) {
          return res["vehicles"];
        } else {
          return res;
        }
      })
    );
  }

  async add(vehicle: Vehicle): Promise<string> {
    let apiVehicle = VehicleUtilities.complyApi(vehicle);
    const url = `${environment.mkgoURL}/api/v1/vehicles`;
    const header = await firstValueFrom(this.dataService.httpOptions(this._isFromMatrix));
    const resp = await this.http.post(url, apiVehicle, header).pipe(first()).toPromise();
    this.timestamps.set(this.dataService.company.id, 0);
    return resp['id'];
  }

  async update(vehicle: Vehicle): Promise<string> {
    const apiVehicle = VehicleUtilities.complyApi(vehicle);
    const url = `${environment.mkgoURL}/api/v1/vehicles/${vehicle.id}`;
    const header = await firstValueFrom(this.dataService.httpOptions(this._isFromMatrix));
    const resp = await this.http.put(url, apiVehicle, header).pipe(first()).toPromise();
    this.timestamps.set(this.dataService.company.id, 0);
    return resp['id']
  }

  /**
   * Get InfoCar usage information for the current company
   * @returns Promise with contractedQueries and consumedQueries
   */
  async getInfoCarUsage(): Promise<{ contractedQueries: number, consumedQueries: number }> {
    const url = `${environment.mkgoURL}/api/v1/vehicles/infocar-usage`;
    const header = await firstValueFrom(this.dataService.httpOptions(this._isFromMatrix));
    const response = await firstValueFrom(this.http.get<{ contractedQueries: number, consumedQueries: number }>(url, header));
    return response;
  }

}
